import type { MissionId, PlayerId, ProfileId, SkillBand } from '../../shared/types/ids';
import type { MissionProgressSnapshot } from '../../shared/types/mission-progress';

/**
 * Whether a saved mission belongs to the child about to play.
 *
 * There is one snapshot slot and the shell offers to carry on with it, so without this gate the
 * offer goes to whoever launches next. A sibling would inherit another child's score, round
 * position and spent teaching moment — and, worse than any of those, the evidence for the rest of
 * the mission would be written against the wrong child and shown to a parent as that child's
 * learning.
 *
 * **Where the identity comes from (D-054(2), Slice R).** It used to be constructible only from
 * live session state, so a browser reload — which empties that state — meant a named or
 * cooperative sitting could not be proved to belong to anybody and started over. The shell now
 * reconstructs it from a governed launch-identity record, and a faithfully rebuilt roster passes
 * these checks. That is deliberate and is what the ruling orders; this guard is unchanged, every
 * refusal below is still live, and the record is *independent* evidence rather than the snapshot
 * restated — so the comparison is still a comparison.
 *
 * The rule is deliberately strict: the same mission, the same number of players, and the same
 * identity for each of them. Anything else starts fresh, and the fresh session's first save
 * overwrites the stale slot — no confirmation screen, because a five-year-old's launch path
 * should not contain a question only an adult can answer.
 *
 * **Accepted bound:** two guests are indistinguishable to the platform (a guest has no profile by
 * definition, D-025), so one guest can resume another guest's mission in the same player slot.
 * The alternative — never resuming for guests — would remove PRO-03 from the "just play" path
 * that has no profile at all.
 */

export interface ResumePlayerIdentity {
  playerId: PlayerId;
  /** Absent for guests, who keep no persistent identity. */
  profileId?: ProfileId;
  /**
   * The band this seat is about to play at (S1-7).
   *
   * Part of the identity of a *sitting*, not of a child: the snapshot's completed content ids name
   * Master questions, so resuming that snapshot into an Explorer sitting replays positions the
   * child has already finished, in a form they never saw, on top of the restored score and round
   * index. A faithfully rebuilt Slice R pair always agrees here — the identity record and the
   * snapshot roster are written from the same `PlayerRuntimeState` at launch — so this refuses
   * only torn or damaged pairs.
   */
  band: SkillBand;
}

export interface ResumeIdentity {
  missionId: MissionId;
  players: readonly ResumePlayerIdentity[];
}

function sameIdentity(
  saved: { playerId: PlayerId; profileId?: ProfileId; band: SkillBand },
  active: ResumePlayerIdentity,
): boolean {
  return (
    saved.playerId === active.playerId &&
    (saved.profileId ?? null) === (active.profileId ?? null) &&
    saved.band === active.band
  );
}

export function canResumeMissionProgress(
  snapshot: MissionProgressSnapshot | null | undefined,
  identity: ResumeIdentity,
): boolean {
  if (snapshot == null) return false;
  if (snapshot.missionId !== identity.missionId) return false;
  if (snapshot.players.length !== identity.players.length) return false;

  // Matched by slot rather than by set: players hold fixed lanes for a whole mission (MVP notes
  // §6), so a snapshot whose heroes are in the other order is not this session's snapshot.
  return snapshot.players.every((saved, index) => {
    const active = identity.players[index];
    return active !== undefined && sameIdentity(saved, active);
  });
}
